import { useCallback, useEffect, useRef, useState } from "react";
import {
  consumeGlobalSearchRequest,
  GLOBAL_SEARCH_EVENT,
  type GlobalSearchRequest,
} from "@/lib/global-search";

import {
  searchHubTargetDisplay,
  openSearchHubOnDisplay,
} from "@/desktop/search-hub-display-routing";
import { restoreAndFocusCurrentWindow } from "@/desktop/use-global-hotkey";
import { isTauri } from "@/lib/is-tauri";

interface OpenHub {
  request: GlobalSearchRequest;
  /** A new id per open, so the hub starts fresh (the shell is keyed on it). */
  openId: number;
}

/** The hub belongs to the app overlay and survives canvas visibility changes. */
export function useGlobalSearchOverlay(currentProjectId: string | null) {
  const [open, setOpen] = useState<OpenHub | null>(null);
  const projectRef = useRef(currentProjectId);
  projectRef.current = currentProjectId;
  const openRef = useRef(open);
  openRef.current = open;
  const nextId = useRef(0);

  useEffect(() => {
    const show = (request: GlobalSearchRequest) => {
      // A borderless display window can paint the hub without becoming key.
      // Match the cross-display path so its input can receive keyboard focus.
      if (isTauri()) restoreAndFocusCurrentWindow();
      setOpen({ request, openId: ++nextId.current });
    };
    function handleKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key !== "k") return;
      event.preventDefault();
      if (openRef.current) {
        setOpen(null);
        return;
      }
      const request: GlobalSearchRequest = {
        chip: "all",
        projectId: projectRef.current,
      };
      const display = searchHubTargetDisplay();
      if (display !== null) openSearchHubOnDisplay(display, request);
      else show(request);
    }
    function handleOpenSearch(event: Event) {
      consumeGlobalSearchRequest();
      show((event as CustomEvent<GlobalSearchRequest>).detail);
    }
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener(GLOBAL_SEARCH_EVENT, handleOpenSearch);
    const pending = consumeGlobalSearchRequest();
    if (pending) show(pending);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener(GLOBAL_SEARCH_EVENT, handleOpenSearch);
    };
  }, []);

  const close = useCallback(() => setOpen(null), []);
  return { request: open?.request ?? null, openId: open?.openId ?? 0, close };
}
